import { Trash2 } from "lucide-react";
import { Link } from "react-router";

import {
  CabeceraDelCajon,
  CampoDelCajon,
  TarjetaDelCajon,
  UsoPorPlantilla,
  type UsoEnOtraPlantilla,
} from "@/components/plantillas/PiezasDelCajon";
import { ResumenDeGeneracionConIa } from "@/components/plantillas/GeneracionConIa";
import {
  BarraDePresencia,
  ChipDeTipo,
  OPCIONES_DE_OBLIGATORIEDAD,
  RotuloDeSeccion,
  Segmentado,
} from "@/components/plantillas/etiquetas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { ItemDeBorrador } from "@/lib/borradorDePlantilla";
import type { Fila, Obligatoriedad } from "@/lib/estructuraDePlantilla";
import { INFORMES_ANALIZADOS, TIPOS_DE_CONTENIDO, type TipoDeContenido } from "@/lib/plantillas";

export type { UsoEnOtraPlantilla };

interface DrawerProps {
  item: ItemDeBorrador | null;
  fila: Fila | null;
  otrasPlantillas: UsoEnOtraPlantilla[];
  /** Fuentes que ya existen en el maestro, para sugerir al escribir. */
  fuentesConocidas: string[];
  onCerrar: () => void;
  onEditar: (
    cambios: Partial<
      Pick<ItemDeBorrador, "nombre" | "descripcion" | "tipo" | "fuente" | "condicionSugerida">
    >,
  ) => void;
  onObligatoriedad: (valor: Obligatoriedad) => void;
  onCondicion: (valor: string) => void;
  /** Cambia el estado del ítem en OTRA plantilla (se guarda en el acto). */
  onCambiarUsoEnOtra: (plantillaId: number, actual: Obligatoriedad | "") => void;
  onPasarAlMaestro: () => void;
  onQuitar: () => void;
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <>
      <span className="text-ink-hint font-semibold">{etiqueta}</span>
      <span className="text-ink-body min-w-0 font-semibold">{children}</span>
    </>
  );
}

/**
 * Cajón de un ítem dentro del editor de una plantilla.
 *
 * Un ítem LOCAL es de esta plantilla y se edita acá. Uno COMPARTIDO pertenece
 * al maestro: acá solo se decide lo que es de ESTA plantilla (su
 * obligatoriedad y su condición, que viajan con "Guardar cambios") y el resto
 * se ve de solo lectura con un enlace al maestro.
 *
 * En "En otras plantillas" el estado de cada una SÍ se cambia desde acá, y se
 * guarda en el acto: no toca el borrador de esta plantilla, escribe las filas
 * de las otras en el servidor.
 */
export function DrawerDeItemDeLaPlantilla({
  item,
  fila,
  otrasPlantillas,
  fuentesConocidas,
  onCerrar,
  onEditar,
  onObligatoriedad,
  onCondicion,
  onCambiarUsoEnOtra,
  onPasarAlMaestro,
  onQuitar,
}: DrawerProps) {
  const abierto = item !== null && fila !== null;
  const usadaEnOtras = otrasPlantillas.filter((u) => u.obligatoriedad).length;

  return (
    <Sheet open={abierto} onOpenChange={(siguiente) => !siguiente && onCerrar()}>
      <SheetContent
        showCloseButton={false}
        className="w-full gap-0 overflow-y-auto sm:max-w-[29.375rem]"
      >
        {item && fila ? (
          <>
            <CabeceraDelCajon titulo={item.nombre} grupo={fila.grupo} subgrupo={fila.subgrupo} />

            <div className="flex flex-col gap-[1.125rem] px-5.5 pt-5 pb-7">
              {item.local ? (
                <>
                  <div className="bg-info-soft text-info-soft-foreground flex flex-col gap-2.5 rounded-lg p-3.5 text-[0.78rem]">
                    <p className="text-[0.8125rem] font-extrabold">Solo en esta plantilla</p>
                    <p className="leading-normal font-medium">
                      Aún no forma parte del maestro, así que ninguna otra plantilla puede usarlo.
                      {item.publicar
                        ? " Pasará al maestro cuando guardes los cambios."
                        : ""}
                    </p>
                    {!item.publicar ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="self-start"
                        onClick={onPasarAlMaestro}
                      >
                        Pasar al maestro
                      </Button>
                    ) : null}
                  </div>

                  <CampoDelCajon id="drawer-nombre" etiqueta="Ítem">
                    <Input
                      id="drawer-nombre"
                      value={item.nombre}
                      onChange={(e) => onEditar({ nombre: e.target.value })}
                    />
                  </CampoDelCajon>
                  <CampoDelCajon id="drawer-descripcion" etiqueta="Descripción / contenido">
                    <Textarea
                      id="drawer-descripcion"
                      rows={3}
                      value={item.descripcion}
                      onChange={(e) => onEditar({ descripcion: e.target.value })}
                    />
                  </CampoDelCajon>
                  <div className="grid grid-cols-2 gap-2.5">
                    <CampoDelCajon id="drawer-tipo" etiqueta="Tipo de contenido">
                      <NativeSelect
                        id="drawer-tipo"
                        value={item.tipo}
                        onChange={(e) => onEditar({ tipo: e.target.value as TipoDeContenido })}
                      >
                        {TIPOS_DE_CONTENIDO.map((t) => (
                          <option key={t.valor} value={t.valor}>
                            {t.etiqueta}
                          </option>
                        ))}
                      </NativeSelect>
                    </CampoDelCajon>
                    <CampoDelCajon id="drawer-fuente" etiqueta="Fuente de información">
                      <Input
                        id="drawer-fuente"
                        list="drawer-fuentes"
                        value={item.fuente}
                        onChange={(e) => onEditar({ fuente: e.target.value })}
                      />
                      <datalist id="drawer-fuentes">
                        {fuentesConocidas.map((f) => (
                          <option key={f} value={f} />
                        ))}
                      </datalist>
                    </CampoDelCajon>
                  </div>
                </>
              ) : (
                <div>
                  <h2 className="text-ink text-[1.25rem] leading-tight font-extrabold tracking-[-0.015em] text-pretty">
                    {item.nombre}
                  </h2>
                  {item.descripcion ? (
                    <p className="text-ink-body mt-1.5 text-[0.84rem] leading-normal font-medium text-pretty">
                      {item.descripcion}
                    </p>
                  ) : null}
                </div>
              )}

              <TarjetaDelCajon>
                <RotuloDeSeccion>
                  {item.local ? "Obligatoriedad" : "En esta plantilla"}
                </RotuloDeSeccion>
                <Segmentado<Obligatoriedad>
                  lleno
                  etiqueta="Obligatoriedad en esta plantilla"
                  valor={fila.obligatoriedad ?? "opcional"}
                  onChange={onObligatoriedad}
                  opciones={OPCIONES_DE_OBLIGATORIEDAD}
                />
                {fila.obligatoriedad === "condicional" ? (
                  <CampoDelCajon id="drawer-condicion" etiqueta="Condición">
                    <Input
                      id="drawer-condicion"
                      value={fila.condicion}
                      onChange={(e) => onCondicion(e.target.value)}
                      placeholder="Ej: Si tiene derechos de agua"
                    />
                  </CampoDelCajon>
                ) : null}
              </TarjetaDelCajon>

              {!item.local ? (
                <>
                  <section className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                      <RotuloDeSeccion tono="hint">Definición</RotuloDeSeccion>
                      {item.id !== null ? (
                        <Link
                          to={`/admin/contenidos/maestro?item=${item.id}`}
                          className="text-primary-fuerte text-xs font-bold hover:underline"
                        >
                          Editar en el maestro
                        </Link>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2.5 text-[0.8125rem]">
                      <Dato etiqueta="Tipo de contenido">
                        <ChipDeTipo tipo={item.tipo} className="text-xs" />
                      </Dato>
                      <Dato etiqueta="Fuente">{item.fuente || "—"}</Dato>
                      <Dato etiqueta="Condición sugerida">{item.condicionSugerida || "—"}</Dato>
                    </div>
                    <p className="text-ink-muted text-xs leading-[1.45] font-medium">
                      {usadaEnOtras > 0
                        ? `Lo usan ${usadaEnOtras + 1} plantillas. Los cambios en el maestro se reflejan en todas.`
                        : "Solo esta plantilla usa este ítem."}
                    </p>
                  </section>

                  <ResumenDeGeneracionConIa
                    tipo={item.tipo}
                    nombre={item.nombre}
                    modo={item.modo}
                    seccionOrigen={item.seccionOrigen}
                    alFinal={item.seRedactaAlFinal}
                    dependencias={item.dependencias}
                    recurso={item.recurso}
                    textoFijo={item.textoFijo}
                  />

                  <UsoPorPlantilla
                    titulo="En otras plantillas"
                    usos={otrasPlantillas}
                    onCiclar={onCambiarUsoEnOtra}
                  />

                  <section className="flex flex-col gap-2">
                    <RotuloDeSeccion tono="hint">Presencia en informes analizados</RotuloDeSeccion>
                    <BarraDePresencia valor={item.frecuencia} total={INFORMES_ANALIZADOS} />
                    <p className="text-ink-muted text-xs leading-[1.45] font-medium">
                      Ejemplo: {item.ejemplo || "—"}
                    </p>
                  </section>
                </>
              ) : null}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 -ml-2 h-8 self-start"
                onClick={onQuitar}
              >
                <Trash2 aria-hidden="true" />
                {item.local ? "Eliminar ítem" : "Quitar de esta plantilla"}
              </Button>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
